import React, { useState } from 'react';
import { Box, Heading, VStack, Text, Checkbox, Flex, HStack, Badge, Icon, Input, InputGroup, InputLeftElement, InputRightElement, IconButton } from '@chakra-ui/react';
import { SearchIcon, CloseIcon } from '@chakra-ui/icons';
import { FaFileInvoice, FaTruck, FaBoxes, FaPlay } from 'react-icons/fa';
import { OrderCardDespacho, getProblemLabels, RetiradaBadge } from '../OrderCardDespacho';
import { labelModoEnvio } from '../../utils/envioMl';

const PROBLEM_GROUP_ORDER = ['AVARIA', 'QUANTIDADE', 'FOTO', 'CÓDIGO', 'MARCA', 'NOME', 'NÃO ESPECIFICADO'];

const SUPPLIER_ORDER = ['MORELATE', 'VANNUCCI', 'BRCOMPANY', 'ISAPA', 'LNG', 'CBA', 'MSAM', 'BLPARTS'];

const SUPPLIER_BORDER_COLORS = {
  MORELATE: 'green.400',
  VANNUCCI: 'blue.400',
  BRCOMPANY: 'yellow.400',
  ISAPA: 'red.400',
  LNG: 'purple.400',
  CBA: 'teal.400',
  MSAM: 'orange.400',
  BLPARTS: 'cyan.400',
  DEFAULT: 'gray.300',
};

// Estilo base das etiquetinhas flutuantes do grupo
const tagStyle = {
  fontSize: "9px",
  px: 2,
  py: 0.5,
  borderTopRadius: "md",
  borderBottomRadius: "0",
  display: "flex",
  alignItems: "center",
  boxShadow: "sm",
  lineHeight: "1.2",
  fontWeight: "bold"
};

// Retorna a chave de agrupamento de um produto (pack ou venda)
const getGroupKey = (p) => p.packId ? `pack-${p.packId}` : `venda-${p.vendaId}`;

// Agrupa produtos por groupKey preservando a ordem
const groupByGroupKey = (products) => {
  const groups = [];
  const indexMap = {};
  products.forEach(p => {
    const key = getGroupKey(p);
    if (indexMap[key] === undefined) {
      indexMap[key] = groups.length;
      groups.push({ key, items: [] });
    }
    groups[indexMap[key]].items.push(p);
  });
  return groups;
};

const GroupedCard = ({
  items,
  borderColor,
  onOpenModal,
  handleAddObs,
  handleChangeTextObs,
  textObs,
  handleApagarObs,
  isSelectable,
  selectedProducts,
  onToggleSelect,
  incompleteVendaIds,
  onProcessIndividual,
  onLinkLalamoveSalvo,
  etapaAtual,
  onProntoRetirada,
}) => {
  const first = items[0];
  const totalItens = items.length;
  const anyIncomplete = incompleteVendaIds
    ? items.some(p => incompleteVendaIds.has(p.vendaId))
    : false;
  const isBlocked = isSelectable && anyIncomplete;

  // Status consolidado da NFe/Etiqueta do grupo (todos compartilham o mesmo)
  const nfeEmitida = items.every(p => p.nfeEmitida);
  const isLalamoveGroup = items.every(p => String(p?.envio?.modo_envio || '').toUpperCase().includes('LALAMOVE'));
  const etiquetaEmitida = items.every(p => p.etiquetaEmitida) || isLalamoveGroup;

  const handleQuickProcess = (e) => {
    e.stopPropagation();
    if (onProcessIndividual && first) onProcessIndividual(first);
  };

  return (
    <Box position="relative" mt={isSelectable ? 4 : 0}>
      {/* Etiquetas flutuantes compartilhadas - Lado Esquerdo */}
      {isSelectable && (
        <HStack
          spacing={0.5}
          position="absolute"
          top="-14px"
          left="8px"
          zIndex={1}
        >
          <Badge
            {...tagStyle}
            variant="solid"
            colorScheme={nfeEmitida ? "green" : "yellow"}
            color={nfeEmitida ? "white" : "yellow.900"}
          >
            <Icon as={FaFileInvoice} mr={0.5} boxSize={2.5} />
            NFE
          </Badge>

          <Badge
            {...tagStyle}
            variant="solid"
            colorScheme={etiquetaEmitida ? (isLalamoveGroup ? "purple" : "green") : "yellow"}
            color={etiquetaEmitida ? "white" : "yellow.900"}
          >
            <Icon as={FaTruck} mr={0.5} boxSize={2.5} />
            {isLalamoveGroup ? "LALAMOVE" : "ETIQ"}
          </Badge>

          <Badge
            {...tagStyle}
            variant="solid"
            colorScheme="purple"
          >
            <Icon as={FaBoxes} mr={0.5} boxSize={2.5} />
            ({totalItens})
          </Badge>

          <RetiradaBadge
            retiradaLoja={first.retiradaLoja}
            prontoRetiradaAt={first.prontoRetiradaAt}
            clienteAvisado={first.clienteAvisadoRetirada}
            isBlocked={isBlocked}
            onClick={onProntoRetirada ? () => onProntoRetirada(first) : null}
          />
        </HStack>
      )}

      {/* Tag de Ação Rápida - Lado Direito */}
      {isSelectable && !isBlocked && (
        <Box
          position="absolute"
          top="-22px"
          right="8px"
          zIndex={2}
        >
          <Badge
            fontSize="11px"
            px={4}
            py={1}
            borderTopRadius="md"
            borderBottomRadius="0"
            display="flex"
            alignItems="center"
            boxShadow="md"
            fontWeight="bold"
            lineHeight="1.2"
            bg="blue.500"
            color="white"
            cursor="pointer"
            _hover={{ bg: "blue.600", transform: "scale(1.05)" }}
            transition="all 0.1s"
            onClick={handleQuickProcess}
          >
            <Icon as={FaPlay} mr={1} boxSize={2.5} />
            PROCESSAR
          </Badge>
        </Box>
      )}

      <Box
        p={2}
        borderWidth="2px"
        borderRadius="lg"
        borderColor={isBlocked ? 'red.400' : borderColor}
        borderStyle={isBlocked ? 'dashed' : 'solid'}
        bg={isBlocked ? 'red.50' : 'blue.50'}
        boxShadow="sm"
        position="relative"
      >
        {isBlocked && (
          <Badge colorScheme="red" position="absolute" top={-2} right={2} fontSize="xs" px={2} py={0.5} borderRadius="full" zIndex={1}>
            ⚠️ Incompleta
          </Badge>
        )}

        {/* Cabeçalho do grupo */}
        <Box px={1} pt={1} pb={2}>
          {first.packId ? (
            <Text fontSize="11px" fontWeight="bold" color="gray.700">
              PACK: <Text as="span" color="blue.700">{first.packId}</Text>
              {' '}
              <Text as="span" color="gray.500" fontWeight="normal">
                ({totalItens} pedidos)
              </Text>
            </Text>
          ) : (
            <Text fontSize="11px" fontWeight="bold" color="gray.700">
              VENDA: <Text as="span" color="blue.700">{first.orderId || first.vendaId}</Text>
              {' '}
              <Text as="span" color="gray.500" fontWeight="normal">
                ({totalItens} itens)
              </Text>
            </Text>
          )}
        </Box>

        <VStack spacing={2} align="stretch">
          {items.map(product => (
            <OrderCardDespacho
              key={product.uniqueId}
              product={product}
              onOpenModal={() => onOpenModal(product)}
              borderColor={borderColor}
              handleAddObs={handleAddObs}
              handleChangeTextObs={handleChangeTextObs}
              textObs={textObs}
              handleApagarObs={handleApagarObs}
              isSelectable={isSelectable}
              isSelected={selectedProducts ? selectedProducts.some(p => p.uniqueId === product.uniqueId) : false}
              onToggleSelect={onToggleSelect}
              isMultiItemVenda={false}
              vendaItemCount={1}
              isIncompleteVenda={incompleteVendaIds ? incompleteVendaIds.has(product.vendaId) : false}
              onProcessIndividual={onProcessIndividual}
              onLinkLalamoveSalvo={onLinkLalamoveSalvo}
              etapaAtual={etapaAtual}
              inGroup={true}
            />
          ))}
        </VStack>
      </Box>
    </Box>
  );
};

export const OrderList = ({ title, products, onOpenModal, status, handleAddObs, handleChangeTextObs, textObs, handleApagarObs, isSelectable, selectedProducts, onToggleSelect, vendaItemCounts, multiItemVendaIds, incompleteVendaIds, onProcessIndividual, columnSelectAll, groupMode = false, onLinkLalamoveSalvo, etapaAtual, kitPacks, onDarEntradaEstoque, onCorrigirImagem, onProntoRetirada }) => {
  const statusBackgrounds = {
    pending: 'gray.100',
    problem: 'red.100',
    verified: 'green.100',
    processed: 'blue.50',
    shipped: 'purple.50',
  };

  const [colSearch, setColSearch] = useState('');
  const term = colSearch.trim().toLowerCase();
  const filteredProducts = term
    ? products.filter(p => {
        const clean = (v) => String(v ?? '').toLowerCase();
        return (
          clean(p.name).includes(term) ||
          clean(p.orderId).includes(term) ||
          clean(p.packId).includes(term) ||
          clean(p.vendaId).includes(term) ||
          clean(p.id).includes(term) ||
          clean(p.globalCode).includes(term) ||
          clean(p.produtoEstoque?.codigo_fornecedor).includes(term) ||
          clean(p.produtoEstoque?.codigo_fabricante).includes(term) ||
          clean(p.produtoEstoque?.codigo_barra).includes(term)
        );
      })
    : products;

  const isProblemColumn = status === 'problem';

  const problemGroups = {};
  if (isProblemColumn) {
    filteredProducts.forEach(product => {
      const labels = getProblemLabels(product);
      const chaves = labels.length > 0 ? labels : ['NÃO ESPECIFICADO'];
      chaves.forEach(chave => {
        if (!problemGroups[chave]) problemGroups[chave] = [];
        problemGroups[chave].push(product);
      });
    });
  }

  const sortedProblems = Object.keys(problemGroups).sort((a, b) => {
    const ia = PROBLEM_GROUP_ORDER.indexOf(a);
    const ib = PROBLEM_GROUP_ORDER.indexOf(b);
    if (ia === -1 && ib === -1) return a.localeCompare(b);
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });

  const groupedData = filteredProducts.reduce((acc, product) => {
    const supplier = product.fornecedor || 'INDEFINIDO';
    const modoEnvioLabel = labelModoEnvio(product.envio);

    if (!acc[supplier]) {
      acc[supplier] = {};
    }

    if (!acc[supplier][modoEnvioLabel]) {
      acc[supplier][modoEnvioLabel] = [];
    }

    acc[supplier][modoEnvioLabel].push(product);

    return acc;
  }, {});

  const sortedSuppliers = Object.keys(groupedData).sort((a, b) => {
    const indexA = SUPPLIER_ORDER.indexOf(a);
    const indexB = SUPPLIER_ORDER.indexOf(b);
    if (indexA === -1 && indexB === -1) return a.localeCompare(b);
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });

  const renderCardsForShippingMode = (shippingProducts, supplier) => {
    const borderColor = SUPPLIER_BORDER_COLORS[supplier] || SUPPLIER_BORDER_COLORS.DEFAULT;

    if (!groupMode) {
      return shippingProducts.map(product => (
        <OrderCardDespacho
          key={product.uniqueId}
          product={product}
          onOpenModal={() => onOpenModal(product)}
          borderColor={borderColor}
          handleAddObs={handleAddObs}
          handleChangeTextObs={handleChangeTextObs}
          textObs={textObs}
          handleApagarObs={handleApagarObs}
          isSelectable={isSelectable}
          isSelected={selectedProducts ? selectedProducts.some(p => p.uniqueId === product.uniqueId) : false}
          onToggleSelect={onToggleSelect}
          isMultiItemVenda={multiItemVendaIds ? multiItemVendaIds.has(product.vendaId) : false}
          vendaItemCount={vendaItemCounts ? vendaItemCounts[product.vendaId] || 1 : 1}
          isIncompleteVenda={incompleteVendaIds ? incompleteVendaIds.has(product.vendaId) : false}
          onProcessIndividual={onProcessIndividual}
          onLinkLalamoveSalvo={onLinkLalamoveSalvo}
          etapaAtual={etapaAtual}
          kitPacks={kitPacks}
          onDarEntradaEstoque={onDarEntradaEstoque}
          onCorrigirImagem={onCorrigirImagem}
          onProntoRetirada={onProntoRetirada}
        />
      ));
    }

    // groupMode: agrupa por packId ou vendaId
    const groups = groupByGroupKey(shippingProducts);
    return groups.map(group => {
      if (group.items.length === 1) {
        const product = group.items[0];
        return (
          <OrderCardDespacho
            key={product.uniqueId}
            product={product}
            onOpenModal={() => onOpenModal(product)}
            borderColor={borderColor}
            handleAddObs={handleAddObs}
            handleChangeTextObs={handleChangeTextObs}
            textObs={textObs}
            handleApagarObs={handleApagarObs}
            isSelectable={isSelectable}
            isSelected={selectedProducts ? selectedProducts.some(p => p.uniqueId === product.uniqueId) : false}
            onToggleSelect={onToggleSelect}
            isMultiItemVenda={multiItemVendaIds ? multiItemVendaIds.has(product.vendaId) : false}
            vendaItemCount={vendaItemCounts ? vendaItemCounts[product.vendaId] || 1 : 1}
            isIncompleteVenda={incompleteVendaIds ? incompleteVendaIds.has(product.vendaId) : false}
            onProcessIndividual={onProcessIndividual}
            onLinkLalamoveSalvo={onLinkLalamoveSalvo}
            etapaAtual={etapaAtual}
            kitPacks={kitPacks}
            onProntoRetirada={onProntoRetirada}
          />
        );
      }

      return (
        <GroupedCard
          key={group.key}
          items={group.items}
          borderColor={borderColor}
          onOpenModal={onOpenModal}
          handleAddObs={handleAddObs}
          handleChangeTextObs={handleChangeTextObs}
          textObs={textObs}
          handleApagarObs={handleApagarObs}
          isSelectable={isSelectable}
          selectedProducts={selectedProducts}
          onToggleSelect={onToggleSelect}
          incompleteVendaIds={incompleteVendaIds}
          onProcessIndividual={onProcessIndividual}
          onLinkLalamoveSalvo={onLinkLalamoveSalvo}
          etapaAtual={etapaAtual}
          onProntoRetirada={onProntoRetirada}
        />
      );
    });
  };

  return (
    <Box bg={statusBackgrounds[status]} p={3} borderRadius="lg" h="100%" minH="500px">
      <Flex align="center" justify="space-between" mb={columnSelectAll ? 2 : 4} px={1}>
        <Heading size="md" textAlign="center" color="gray.700" flex={1}>
          {title}
        </Heading>
      </Flex>

      {/* Checkbox de seleção por coluna */}
      {columnSelectAll && columnSelectAll.count > 0 && (
        <Box
          bg="white"
          px={3}
          py={2}
          mb={3}
          borderRadius="md"
          borderWidth="1px"
          borderColor="gray.200"
          shadow="xs"
        >
          <Checkbox
            isChecked={columnSelectAll.isChecked}
            isIndeterminate={columnSelectAll.isIndeterminate}
            onChange={columnSelectAll.onChange}
            colorScheme="blue"
            size="md"
          >
            <Text fontSize="sm" color="gray.600" fontWeight="medium">
              Selecionar todos ({columnSelectAll.count})
            </Text>
          </Checkbox>
        </Box>
      )}

      {products.length > 0 && (
        <InputGroup size="sm" mb={3}>
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.400" boxSize={3} />
          </InputLeftElement>
          <Input
            placeholder="Buscar nesta coluna..."
            value={colSearch}
            onChange={(e) => setColSearch(e.target.value)}
            bg="white"
            focusBorderColor="orange.300"
            pr="2rem"
            borderRadius="md"
          />
          {colSearch && (
            <InputRightElement>
              <IconButton
                aria-label="Limpar busca"
                icon={<CloseIcon boxSize={2} />}
                size="xs"
                isRound
                variant="ghost"
                onClick={() => setColSearch('')}
              />
            </InputRightElement>
          )}
        </InputGroup>
      )}

      {products.length === 0 ? (
        <Text textAlign="center" color="gray.500" fontSize="sm" mt={4}>Nenhum produto aqui.</Text>
      ) : filteredProducts.length === 0 ? (
        <Text textAlign="center" color="gray.500" fontSize="sm" mt={4}>Nenhum resultado para a busca.</Text>
      ) : isProblemColumn ? (
        <VStack spacing={4} align="stretch">
          {sortedProblems.map(problema => (
            <Box key={problema} mb={2}>
              <Heading
                size="sm"
                color="gray.600"
                mb={2}
                px={2}
                borderBottomWidth="1px"
                borderColor="gray.300"
                pb={1}
                textTransform="uppercase"
              >
                {problema} ({problemGroups[problema].length})
              </Heading>

              <VStack spacing={2} align="stretch" px={1}>
                {problemGroups[problema].map(product => (
                  <OrderCardDespacho
                    key={product.uniqueId}
                    product={product}
                    onOpenModal={() => onOpenModal(product)}
                    borderColor={SUPPLIER_BORDER_COLORS[product.fornecedor] || SUPPLIER_BORDER_COLORS.DEFAULT}
                    handleAddObs={handleAddObs}
                    handleChangeTextObs={handleChangeTextObs}
                    textObs={textObs}
                    handleApagarObs={handleApagarObs}
                    isSelectable={isSelectable}
                    isSelected={selectedProducts ? selectedProducts.some(p => p.uniqueId === product.uniqueId) : false}
                    onToggleSelect={onToggleSelect}
                    isMultiItemVenda={multiItemVendaIds ? multiItemVendaIds.has(product.vendaId) : false}
                    vendaItemCount={vendaItemCounts ? vendaItemCounts[product.vendaId] || 1 : 1}
                    isIncompleteVenda={incompleteVendaIds ? incompleteVendaIds.has(product.vendaId) : false}
                    onProcessIndividual={onProcessIndividual}
                    onLinkLalamoveSalvo={onLinkLalamoveSalvo}
                    etapaAtual={etapaAtual}
                    kitPacks={kitPacks}
                    onDarEntradaEstoque={onDarEntradaEstoque}
                    onCorrigirImagem={onCorrigirImagem}
                    showProblemTags
                  />
                ))}
              </VStack>
            </Box>
          ))}
        </VStack>
      ) : (
        <VStack spacing={4} align="stretch">
          {sortedSuppliers.map(supplier => (
            <Box key={supplier} mb={2}>
              {/* NÍVEL 1: Título do Fornecedor */}
              <Heading
                size="sm"
                color="gray.600"
                mb={2}
                px={2}
                borderBottomWidth="1px"
                borderColor="gray.300"
                pb={1}
                textTransform="uppercase"
              >
                {supplier}
              </Heading>

              <VStack spacing={3} align="stretch">
                {Object.keys(groupedData[supplier]).sort().map(shippingMode => (
                  <Box key={shippingMode} pl={1}>
                    {/* NÍVEL 2: Subtítulo do Modo de Envio */}
                    <Text
                      fontSize="xs"
                      fontWeight="bold"
                      color="gray.500"
                      mb={2}
                      ml={1}
                      textTransform="uppercase"
                      letterSpacing="wide"
                    >
                      • {shippingMode}
                    </Text>

                    {/* NÍVEL 3: Lista de Cards (agrupados ou individuais) */}
                    <VStack spacing={2} align="stretch" px={1}>
                      {renderCardsForShippingMode(groupedData[supplier][shippingMode], supplier)}
                    </VStack>
                  </Box>
                ))}
              </VStack>
            </Box>
          ))}
        </VStack>
      )}
    </Box>
  );
};
